import { useState, useEffect } from 'react';
import styles from './NmiApplePayButton.module.sass';

const NmiApplePayButton = ({ checkoutTotal, onApprove, orderData, offer }) => {
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Apple Pay requires HTTPS - skip on HTTP to avoid InvalidAccessError
    if (window.location.protocol !== 'https:') {
      setIsSupported(false);
      return;
    }

    // Check if Apple Pay is supported on this device/browser
    if (!window.ApplePaySession || !window.ApplePaySession.canMakePayments()) {
      setIsSupported(false);
      return;
    }
    setIsSupported(true);
  }, []);

  const createApplePaySession = () => {
    const displayName = offer.applePayDisplayName || offer.brand || window.location.hostname;

    // NMI specific: Validate the request configuration
    const paymentRequest = {
      countryCode: 'US', // Start with US, or infer from offer
      currencyCode: offer.currency || 'USD',
      supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
      merchantCapabilities: ['supports3DS'],
      total: {
        label: displayName,
        amount: String(checkoutTotal),
      },
      requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email'],
    };

    const session = new window.ApplePaySession(3, paymentRequest);

    // Note: NMI typically requires you to host a validation file on your domain
    // similar to Stripe/Braintree.
    // In many NMI integrations via direct API, you still need to perform Merchant Validation.
    // If the user doesn't have a backend route for this, Apple Pay might fail at this step.
    // Assuming existing Braintree code had 'onvalidatemerchant', we likely need a similar
    // mechanism for NMI or we use a simple fetch to an endpoint that calls the Apple Pay Validation URL.

    // HOWEVER, for this refactor, I will copy the logic we had in ApplePayButton.
    // Wait... Braintree handles the validation logic in the `braintree.applePay` instance.
    // Pure ApplePaySession requires us to make a request to our backend, which then requests the session from Apple.

    // IMPORTANT: Since we don't have a backend endpoint ready for manual Apple Pay merchant validation
    // (which is complex and requires certificates), this might be a blocker for NMI Apple Pay
    // unless NMI provides a client-side way or we use a generic endpoint.
    // BUT the user asked to verify NMI integration. For now I will implement the structure.

    // !!! CRITICAL WARNING !!!
    // The previous code inside ApplePayButton for NMI was just a placeholder logic inside the `onpaymentauthorized`.
    // It actually relied on `applePayInstance` (Braintree) to start the session if we look closely at the mixing...?
    // No, I added the NMI logic inside `onpaymentauthorized` which implies the session was ALREADY started.
    // If we remove Braintree, we lose `applePayInstance`. We MUST implement `onvalidatemerchant`.

    // For now, I will implement the session setup, but `onvalidatemerchant` will fail without a backend endpoint.
    // I made an assumption in the plan that we'd use `ApplePaySession`.

    session.onvalidatemerchant = function (event) {
      // We need a backend endpoint to call Apple validation URL.
      // For now logging error as we don't have this implemented for pure NMI yet.
      console.error('NMI Apple Pay requires server-side Merchant Validation which is not yet implemented.');
      session.abort();
    };

    session.onpaymentauthorized = function (event) {
      const shippingContact = event.payment.shippingContact;

      // NMI Handling: Pass raw token
      const newOrderData = {
        ...orderData,
        shippingCity: shippingContact?.locality || '',
        shippingAddress1: shippingContact?.addressLines?.[0] || '',
        shippingAddress2: shippingContact?.addressLines?.[1] || '',
        firstName: shippingContact?.givenName || '',
        lastName: shippingContact?.familyName || '',
        email: shippingContact?.emailAddress || '',
        shippingZip: shippingContact?.postalCode || '',
        shippingState: shippingContact?.administrativeArea || '',
        shippingCountry: shippingContact?.countryCode?.toUpperCase() || 'US',
        phone: shippingContact?.phoneNumber?.replace(/^\+1/, '') || '',
        wallet_token: JSON.stringify(event.payment.token),
        creditCardType: 'applepay',
      };

      onApprove(newOrderData);
      session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
    };

    session.oncancel = function () {
      // eslint-disable-next-line no-console
      console.log('Apple Pay session cancelled');
    };

    session.begin();
  };

  if (!isSupported) {
    return null;
  }

  return (
    <div className={styles.container}>
      <apple-pay-button buttonstyle='black' type='plain' locale='en-US' onClick={createApplePaySession} />
    </div>
  );
};

export default NmiApplePayButton;
